import { Disclosure } from '@bcecloud/portal-ui';
import { HelpCircleIcon } from 'lucide-react';
import { BCE_PHONE } from '@/components/PayCheckout';

/**
 * A short "how this works" for a My Events tab: closed until the client wants
 * it, so the tab's own job (sign, pay) stays first. The same shape on every
 * tab, so help is always in the same place.
 */

export interface HowItWorksProps {
  /** What the row says while closed: "How signing works". */
  readonly title: string;
  /** The steps, in the order the client meets them. */
  readonly steps: readonly string[];
  /** One line after the steps, for the question people ask most. */
  readonly note?: string;
}

/**
 * @param props - The title, the steps and an optional closing note.
 * @returns The help row.
 */
export function HowItWorks({ title, steps, note }: HowItWorksProps): React.ReactElement {
  return (
    <Disclosure
      summary={
        <span className="flex items-center gap-2 text-portal-small font-bold text-portal-ink">
          <HelpCircleIcon aria-hidden className="size-4 text-brand" />
          {title}
        </span>
      }
    >
      <ol className="list-decimal space-y-1.5 pl-5 text-portal-small text-portal-ink">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {note ? <p className="mt-3 text-portal-small text-portal-muted">{note}</p> : null}
      <p className="mt-3 text-portal-small text-portal-muted">
        Questions? Reply to any email from Best Corporate Events, or call{' '}
        <a className="whitespace-nowrap text-brand-link underline" href={`tel:${BCE_PHONE}`}>
          {BCE_PHONE}
        </a>
        .
      </p>
    </Disclosure>
  );
}

/** The Contracts tab's help. */
export const SIGNING_HELP: HowItWorksProps = {
  title: 'How signing works',
  steps: [
    'Choose Preview to read the contract, or Sign to open it for signing.',
    'Add your initials and signature where the contract asks for them, then finish.',
    'Best Corporate Events signs next. Nothing more is needed from you while that happens.',
    'When both signatures are on it, the contract shows Signed and we email you. Download your copy here any time.',
  ],
  note: 'Any contact on this event can sign. It does not have to be the person who got the email.',
};

/** The Payment tab's help. */
export const PAYING_HELP: HowItWorksProps = {
  title: 'How paying works',
  steps: [
    'Choose Pay now beside a line. Or, under Make a payment, pay the full balance or another amount.',
    'Pay by bank account (ACH) or by card.',
    'A card payment shows here right away. A bank payment takes about four business days to clear, and your balance updates when it does. There is no need to pay again.',
    'Your receipt is emailed to you after each payment.',
  ],
  note: 'Does someone else pay the bills? Ask us for a payment link. They can pay with it without signing in.',
};
